import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from './utils/experienceStore';
import {
    addProcessorNode,
    setCanvasPosition,
    setCanvasZoom,
    selectProcessorNode,
    setDragState,
    addConnection,
} from './utils/state/processingFlowSlice';
import { Processor } from '../Processing/components/hooks/useProcessing';
import ProcessorNode from './ProcessorNode';

interface ProcessingFlowCanvasProps {
    width: number;
    height: number;
    processors: Processor[];
}

export default function ProcessingFlowCanvas({
    width,
    height,
    processors,
}: ProcessingFlowCanvasProps) {
    const dispatch = useDispatch();
    const canvasRef = useRef<HTMLDivElement>(null);
    const [isPanning, setIsPanning] = useState(false);
    const [panStart, setPanStart] = useState({ x: 0, y: 0 });
    const [connectionStart, setConnectionStart] = useState<string | null>(null);
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

    const {
        nodes,
        connections,
        selectedNodeId,
        canvasPosition,
        canvasZoom,
        isDragging,
        draggedNodeType,
        dragHoverPosition,
    } = useSelector((state: RootState) => state.flows.present);

    // Handle canvas panning
    const handleMouseDown = useCallback(
        (e: React.MouseEvent) => {
            if (e.button === 0) {
                // Left mouse button behavior
                e.preventDefault();

                // If clicking on empty canvas, deselect and enable panning
                if (e.target === canvasRef.current) {
                    dispatch(selectProcessorNode(undefined));
                    // Only start panning if no node is selected
                    setIsPanning(true);
                    setPanStart({
                        x: e.clientX - canvasPosition.x,
                        y: e.clientY - canvasPosition.y,
                    });
                } else if (!selectedNodeId) {
                    // If no node is selected and clicking elsewhere, enable panning
                    setIsPanning(true);
                    setPanStart({
                        x: e.clientX - canvasPosition.x,
                        y: e.clientY - canvasPosition.y,
                    });
                }
                // If a node is selected, don't start panning (let node handle the interaction)
            }
        },
        [canvasPosition, dispatch, selectedNodeId],
    );

    const handleMouseMove = useCallback(
        (e: React.MouseEvent) => {
            setMousePosition({ x: e.clientX, y: e.clientY });

            if (isPanning) {
                dispatch(
                    setCanvasPosition({
                        x: e.clientX - panStart.x,
                        y: e.clientY - panStart.y,
                    }),
                );
            }

            // Update drag hover position for visual feedback
            if (isDragging) {
                const rect = canvasRef.current?.getBoundingClientRect();
                if (rect) {
                    const canvasX =
                        (e.clientX - rect.left - canvasPosition.x) / canvasZoom;
                    const canvasY =
                        (e.clientY - rect.top - canvasPosition.y) / canvasZoom;

                    dispatch(
                        setDragState({
                            isDragging: true,
                            draggedNodeType,
                            dragHoverPosition: { x: canvasX, y: canvasY },
                        }),
                    );
                }
            }
        },
        [
            isPanning,
            panStart,
            isDragging,
            draggedNodeType,
            canvasPosition,
            canvasZoom,
            dispatch,
        ],
    );

    const handleMouseUp = useCallback(() => {
        setIsPanning(false);
        setPanStart({ x: 0, y: 0 });
        setConnectionStart(null);
    }, []);

    // Handle zoom
    const handleWheel = useCallback(
        (e: React.WheelEvent) => {
            e.preventDefault();
            const delta = e.deltaY > 0 ? 0.9 : 1.1;
            const newZoom = Math.max(0.1, Math.min(3, canvasZoom * delta));
            dispatch(setCanvasZoom(newZoom));
        },
        [canvasZoom, dispatch],
    );

    // Handle drag and drop from processor palette
    const handleDrop = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault();

            try {
                const processorData = JSON.parse(
                    e.dataTransfer.getData('application/json'),
                );
                const processor = processors.find(
                    (p) => p.name === processorData.name,
                );

                if (processor) {
                    const rect = canvasRef.current?.getBoundingClientRect();
                    if (rect) {
                        const canvasX =
                            (e.clientX - rect.left - canvasPosition.x) /
                            canvasZoom;
                        const canvasY =
                            (e.clientY - rect.top - canvasPosition.y) /
                            canvasZoom;

                        dispatch(
                            addProcessorNode({
                                processor,
                                position: { x: canvasX - 100, y: canvasY - 60 }, // Center the node
                            }),
                        );
                    }
                }
            } catch (error) {
                console.error('Error parsing dropped processor data:', error);
            }

            dispatch(
                setDragState({
                    isDragging: false,
                    draggedNodeType: undefined,
                    dragHoverPosition: undefined,
                }),
            );
        },
        [processors, canvasPosition, canvasZoom, dispatch],
    );

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
    }, []);

    // Connection handling
    const handleStartConnection = useCallback((nodeId: string) => {
        setConnectionStart(nodeId);
    }, []);

    const handleEndConnection = useCallback(
        (nodeId: string) => {
            if (connectionStart && connectionStart !== nodeId) {
                // Check if connection already exists
                const existingConnection = connections.find(
                    (conn) =>
                        conn.sourceId === connectionStart &&
                        conn.targetId === nodeId,
                );

                if (!existingConnection) {
                    dispatch(
                        addConnection({
                            sourceId: connectionStart,
                            targetId: nodeId,
                        }),
                    );
                }
            }
            setConnectionStart(null);
        },
        [connectionStart, connections, dispatch],
    );

    // Render connection lines
    const renderConnections = () => {
        return connections.map((connection) => {
            const sourceNode = nodes.find((n) => n.id === connection.sourceId);
            const targetNode = nodes.find((n) => n.id === connection.targetId);

            if (!sourceNode || !targetNode) return null;

            // Apply canvas transformations (pan and zoom)
            const sourceX =
                (sourceNode.position.x + 200) * canvasZoom + canvasPosition.x; // Right edge of source node
            const sourceY =
                (sourceNode.position.y + 60) * canvasZoom + canvasPosition.y; // Middle of source node
            const targetX =
                targetNode.position.x * canvasZoom + canvasPosition.x; // Left edge of target node
            const targetY =
                (targetNode.position.y + 60) * canvasZoom + canvasPosition.y; // Middle of target node

            // Create smooth curved path
            const dx = targetX - sourceX;
            const controlOffset = Math.min(
                Math.abs(dx) * 0.5,
                100 * canvasZoom,
            ); // Scale curve with zoom

            const controlPoint1X = sourceX + controlOffset;
            const controlPoint1Y = sourceY;
            const controlPoint2X = targetX - controlOffset;
            const controlPoint2Y = targetY;

            const pathData = `M ${sourceX} ${sourceY} C ${controlPoint1X} ${controlPoint1Y}, ${controlPoint2X} ${controlPoint2Y}, ${targetX} ${targetY}`;

            return (
                <path
                    key={connection.id}
                    d={pathData}
                    stroke="#3B82F6"
                    strokeWidth="2"
                    fill="none"
                    markerEnd="url(#arrowhead)"
                    className="drop-shadow-sm"
                />
            );
        });
    };

    // Render temporary connection line while dragging
    const renderTemporaryConnection = () => {
        if (!connectionStart) return null;

        const sourceNode = nodes.find((n) => n.id === connectionStart);
        if (!sourceNode) return null;

        // Apply canvas transformations for source node
        const sourceX =
            (sourceNode.position.x + 200) * canvasZoom + canvasPosition.x;
        const sourceY =
            (sourceNode.position.y + 60) * canvasZoom + canvasPosition.y;

        // Mouse position is already in screen coordinates, no transformation needed
        const targetX =
            mousePosition.x - canvasRef.current!.getBoundingClientRect().left;
        const targetY =
            mousePosition.y - canvasRef.current!.getBoundingClientRect().top;

        const dx = targetX - sourceX;
        const controlOffset = Math.min(Math.abs(dx) * 0.5, 100 * canvasZoom); // Scale curve with zoom

        const controlPoint1X = sourceX + controlOffset;
        const controlPoint1Y = sourceY;
        const controlPoint2X = targetX - controlOffset;
        const controlPoint2Y = targetY;

        const pathData = `M ${sourceX} ${sourceY} C ${controlPoint1X} ${controlPoint1Y}, ${controlPoint2X} ${controlPoint2Y}, ${targetX} ${targetY}`;

        return (
            <path
                d={pathData}
                stroke="#94A3B8"
                strokeWidth="2"
                strokeDasharray="5,5"
                fill="none"
                className="pointer-events-none"
            />
        );
    };

    // Grid background
    const renderGrid = () => {
        const gridSize = 20 * canvasZoom;
        const offsetX = canvasPosition.x % gridSize;
        const offsetY = canvasPosition.y % gridSize;

        const lines = [];

        // Vertical lines
        for (let x = offsetX; x < width; x += gridSize) {
            lines.push(
                <line
                    key={`v-${x}`}
                    x1={x}
                    y1={0}
                    x2={x}
                    y2={height}
                    stroke="#F1F5F9"
                    strokeWidth="1"
                />,
            );
        }

        // Horizontal lines
        for (let y = offsetY; y < height; y += gridSize) {
            lines.push(
                <line
                    key={`h-${y}`}
                    x1={0}
                    y1={y}
                    x2={width}
                    y2={y}
                    stroke="#F1F5F9"
                    strokeWidth="1"
                />,
            );
        }

        return lines;
    };

    // Event listeners
    useEffect(() => {
        const handleGlobalMouseUp = () => handleMouseUp();
        document.addEventListener('mouseup', handleGlobalMouseUp);
        return () =>
            document.removeEventListener('mouseup', handleGlobalMouseUp);
    }, [handleMouseUp]);

    return (
        <div
            ref={canvasRef}
            className="relative w-full h-full overflow-hidden bg-muted/50 cursor-grab active:cursor-grabbing"
            style={{ width, height }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onWheel={handleWheel}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
        >
            {/* Grid Background */}
            <svg
                className="absolute inset-0 pointer-events-none"
                width={width}
                height={height}
            >
                <defs>
                    <marker
                        id="arrowhead"
                        markerWidth="10"
                        markerHeight="7"
                        refX="9"
                        refY="3.5"
                        orient="auto"
                    >
                        <polygon points="0 0, 10 3.5, 0 7" fill="#3B82F6" />
                    </marker>
                </defs>
                {renderGrid()}
            </svg>

            {/* Canvas Content */}
            <div
                className="absolute"
                style={{
                    transform: `translate(${canvasPosition.x}px, ${canvasPosition.y}px) scale(${canvasZoom})`,
                    transformOrigin: '0 0',
                }}
            >
                {/* Connection Lines */}
                <svg
                    className="absolute inset-0 pointer-events-none"
                    style={{
                        width: '100vw',
                        height: '100vh',
                        left: -canvasPosition.x / canvasZoom,
                        top: -canvasPosition.y / canvasZoom,
                    }}
                >
                    {renderConnections()}
                    {renderTemporaryConnection()}
                </svg>

                {/* Processor Nodes */}
                {nodes.map((node) => (
                    <ProcessorNode
                        key={node.id}
                        node={node}
                        isSelected={selectedNodeId === node.id}
                        onStartConnection={handleStartConnection}
                        onEndConnection={handleEndConnection}
                        scale={canvasZoom}
                    />
                ))}

                {/* Drop Zone Indicator */}
                {isDragging && dragHoverPosition && (
                    <div
                        className="absolute border-2 border-dashed border-blue-400 bg-blue-50 rounded-lg pointer-events-none"
                        style={{
                            left: dragHoverPosition.x - 100,
                            top: dragHoverPosition.y - 60,
                            width: 200,
                            height: 120,
                        }}
                    />
                )}
            </div>

            {/* Canvas Controls - Match Experience Editor Style */}
            <div className="absolute top-4 right-4 flex items-center space-x-2">
                <div className="bg-background border border-border rounded-lg shadow-sm flex items-center">
                    <button
                        onClick={() =>
                            dispatch(
                                setCanvasZoom(Math.max(0.1, canvasZoom * 0.8)),
                            )
                        }
                        className="px-3 py-2 text-sm font-medium text-foreground hover:bg-muted/50 border-r border-border"
                        title="Zoom Out"
                    >
                        −
                    </button>
                    <div className="px-3 py-2 text-sm text-muted-foreground min-w-[60px] text-center">
                        {Math.round(canvasZoom * 100)}%
                    </div>
                    <button
                        onClick={() =>
                            dispatch(
                                setCanvasZoom(Math.min(3, canvasZoom * 1.2)),
                            )
                        }
                        className="px-3 py-2 text-sm font-medium text-foreground hover:bg-muted/50 border-l border-border"
                        title="Zoom In"
                    >
                        +
                    </button>
                </div>
                <button
                    onClick={() => {
                        dispatch(setCanvasZoom(1));
                        dispatch(setCanvasPosition({ x: 0, y: 0 }));
                    }}
                    className="bg-background border border-border rounded-lg px-3 py-2 text-sm font-medium text-foreground hover:bg-muted/50 shadow-sm"
                    title="Reset View"
                >
                    Reset
                </button>
            </div>
        </div>
    );
}
